/* Arabic for an adhkar / ruqyah item in the chosen script, optionally with
   tajweed colours.

   Qurʾānic items (Āyat al-Kursī, the Quls…) use the official Qurʾān text in
   the encoding the chosen script's font is made for (quran/script.ts): the
   King Fahd Uthmani text for Uthmani, the IndoPak Nastaleeq text for IndoPak,
   Quran.com's Uthmani text for Naskh and Amiri — and Quran.com's published
   tajweed, aligned onto that text. Duʿāʾ use their own text, with tajweed from
   the conservative rules engine (duaTajweed.ts). Tajweed colours use the
   Uthmani script, since the published markup is only for that script. */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Palette, Type } from 'lucide-react';
import { useSettings } from '@/lib/store';
import type { AdhkarScript } from '@/lib/types';
import { bundledVerseTexts, verseTexts, type VerseTexts } from '@/features/quran/api';
import { TajweedLegend, TajweedText } from '@/features/quran/Tajweed';
import { IconButton, Row, Section, Segmented, Sheet, Stepper, Switch } from '@/components/ui';
import { alignTajweed } from '@/features/quran/tajweedAlign';
import { indopakVerse, qpcVerse, uthmaniVerse } from '@/features/quran/script';
import { duaTajweedMarkup } from './duaTajweed';

/* Taʿawwudh in each encoding, from the muṣḥaf's own words (113:1, 16:98). */
const TAAWWUDH: Record<'qpc' | 'indopak' | 'uthmani', string> = {
  qpc: 'أَعُوذُ بِٱللَّهِ مِنَ ٱلشَّيۡطَٰنِ ٱلرَّجِيمِ',
  indopak: 'اَعُوْذُ بِاللّٰهِ مِنَ الشَّیْطٰنِ الرَّجِیْمِ',
  uthmani: 'أَعُوذُ بِٱللَّهِ مِنَ ٱلشَّيْطَـٰنِ ٱلرَّجِيمِ',
};
const arabicDigits = (n: number) => String(n).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[Number(d)]);

/* "[[need]]" in a duʿāʾ marks where you name your own matter (Istikhārah). */
export const NEED = '[[need]]';
export function NeedPill({ lang }: { lang: 'ar' | 'en' }) {
  return (
    <span className="mx-1 inline-block rounded-lg bg-gold/20 px-2 py-0.5 align-middle font-sans text-[13px] font-semibold not-italic leading-snug text-gold ring-1 ring-gold/40" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {lang === 'ar' ? 'اذكر حاجتك' : 'name your matter'}
    </span>
  );
}
/** Text with each [[need]] marker shown as the highlighted pill. */
export function WithNeed({ text, lang = 'en', render = (s: string) => s }: { text: string; lang?: 'ar' | 'en'; render?: (s: string) => React.ReactNode }) {
  if (!text.includes(NEED)) return <>{render(text)}</>;
  const parts = text.split(NEED);
  return <>{parts.map((p, i) => <span key={i}>{render(p)}{i < parts.length - 1 && <NeedPill lang={lang} />}</span>)}</>;
}

export const SCRIPTS: { value: AdhkarScript; label: string }[] = [
  { value: 'naskh', label: 'Naskh' },
  { value: 'amiri', label: 'Amiri' },
  { value: 'uthmani', label: 'Uthmani' },
  { value: 'indopak', label: 'IndoPak' },
];

/** Which encoding a script's font is made for (see quran/script.ts). */
const encodingFor = (script: AdhkarScript, tajweed: boolean, verses?: VerseTexts[]) =>
  script === 'uthmani' || (script === 'indopak' && (tajweed || !verses?.every((v) => v.text_indopak_nastaleeq))) ? 'qpc'
    : script === 'indopak' ? 'indopak' : 'uthmani';
const VERSE_TEXT = {
  qpc: (v: VerseTexts) => qpcVerse(v),
  indopak: (v: VerseTexts) => indopakVerse(v) ?? qpcVerse(v),
  uthmani: (v: VerseTexts) => uthmaniVerse(v),
};

export function AdhkarArabic({ ar, quran, taawwudh, base = 22 }: { ar: string; quran?: string[]; taawwudh?: boolean; base?: number }) {
  const { adhkarScript: script, adhkarTajweed: tajweed, arabicSize } = useSettings((s) => s.settings);
  const verses = useQuery({ queryKey: ['verse-texts', quran?.join(',')], queryFn: () => verseTexts(quran!), enabled: !!quran?.length, staleTime: Infinity, retry: 1, initialData: () => (quran?.length ? bundledVerseTexts(quran) : undefined) });
  const style = { fontSize: `${base * arabicSize}px` };

  // Qurʾānic item, once its official text has loaded (until then — or offline — the stored text).
  if (quran && verses.data) {
    const enc = encodingFor(script, tajweed, verses.data);
    // IndoPak with tajweed colours (or without the Nastaleeq text) is drawn in the Uthmani font.
    const fontClass = enc === 'qpc' ? 'ar-uthmani' : `ar-${script}`;
    const text = VERSE_TEXT[enc];
    return (
      <p className={`${fontClass} leading-[2.1]`} dir="rtl" lang="ar" style={style}>
        {taawwudh && (tajweed ? <><TajweedText html={duaTajweedMarkup(TAAWWUDH[enc])} />{' '}</> : <>{TAAWWUDH[enc]}{' '}</>)}
        {verses.data.map((v) => {
          const n = arabicDigits(Number(v.verse_key.split(':')[1]));
          return (
            <span key={v.verse_key}>
              {tajweed && v.text_uthmani_tajweed ? <TajweedText html={alignTajweed(text(v), v.text_uthmani_tajweed, false)} /> : text(v)}
              <span className="ayah-num"> ﴿{n}﴾ </span>
            </span>
          );
        })}
      </p>
    );
  }
  const fontClass = `ar-${script}`;
  if (!ar) return <p className="py-3 text-center text-[13px] text-muted">{verses.isError ? 'Could not load the text — check your connection.' : 'Loading…'}</p>;
  return (
    <p className={`${fontClass} leading-[2.1]`} dir="rtl" lang="ar" style={style}>
      <WithNeed text={ar} lang="ar" render={(s) => (tajweed ? <TajweedText html={duaTajweedMarkup(s)} /> : s)} />
    </p>
  );
}

/** Header button + sheet: script, tajweed, size, translation. */
export function AdhkarDisplayButton({ showTr, setShowTr }: { showTr?: boolean; setShowTr?: (v: boolean) => void }) {
  const [open, setOpen] = useState(false);
  const [legend, setLegend] = useState(false);
  const { adhkarScript, adhkarTajweed, arabicSize } = useSettings((s) => s.settings);
  const update = useSettings((s) => s.update);
  const sample = useMemo(() => 'بِسْمِ ٱللَّهِ ٱلَّذِى لَا يَضُرُّ مَعَ ٱسْمِهِۦ شَىْءٌ', []);
  return (
    <>
      <IconButton label="Arabic display" onClick={() => setOpen(true)}><Type size={18} /></IconButton>
      <Sheet open={open} onClose={() => setOpen(false)} title="Arabic display">
        <div className="space-y-4 px-5 pb-2">
          <div className="rounded-2xl bg-fill p-4"><AdhkarArabic ar={sample} base={22} /></div>
          <Segmented label="Script" value={adhkarScript} onChange={(v) => update({ adhkarScript: v })} options={SCRIPTS} />
        </div>
        <Section footer="Qurʾān verses use Quran.com's tajweed muṣḥaf colours. Duʿāʾ are coloured only where the vowel marks make a rule certain, so some rules are left uncoloured.">
          <Row icon={<Palette size={18} />} label="Tajweed colours"><Switch label="Tajweed colours" checked={adhkarTajweed} onChange={(v) => update({ adhkarTajweed: v })} /></Row>
          {adhkarTajweed && <Row label="Colour key" onClick={() => setLegend(true)} />}
          <Row label="Arabic size"><Stepper value={arabicSize} min={0.8} max={1.8} step={0.1} onChange={(v) => update({ arabicSize: Math.round(v * 10) / 10 })} format={(v) => `${Math.round(v * 100)}%`} /></Row>
          {setShowTr && <Row label="Transliteration & translation"><Switch label="Transliteration & translation" checked={!!showTr} onChange={setShowTr} /></Row>}
        </Section>
      </Sheet>
      <TajweedLegend open={legend} onClose={() => setLegend(false)} />
    </>
  );
}
